import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Button, useTheme } from '@geist-ui/core';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import './TextureViewer3D.css';

const TextureViewer3D = ({ maps, activeMap, textureColor, texture: textureData, onMapAvailabilityChange }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const composerRef = useRef(null);
  const planeMeshRef = useRef(null);
  const sphereMeshRef = useRef(null);
  const activeMeshRef = useRef(null);
  const directionalLightRef = useRef(null);
  const ambientLightRef = useRef(null);
  const requestRef = useRef(null);
  
  const textureIdRef = useRef(textureData?.id || 'default');
  
  // 3D tilt control variables
  const [is3dEnabled, setIs3dEnabled] = useState(true);
  const [isShapeSphere, setIsShapeSphere] = useState(false);
  const [isHDREnabled, setIsHDREnabled] = useState(true);
  const currentTiltXRef = useRef(0);
  const currentTiltYRef = useRef(0);
  const targetTiltXRef = useRef(0);
  const targetTiltYRef = useRef(0);
  
  // Sphere manual rotation controls
  const isDraggingRef = useRef(false);
  const previousMousePositionRef = useRef({ x: 0, y: 0 });
  const sphereRotationRef = useRef({ x: 0, y: 0 });
  const autoRotateRef = useRef(true);
  
  // Default values - reduced metalness to better respect the maps
  const DEFAULT_METALLIC = 0.05; // Reduced from 0.3 to minimize reflections
  const DEFAULT_ROUGHNESS = 0.8; // Increased from 0.7 for less reflectivity
  const DEFAULT_TILING = 1;
  const DEFAULT_LIGHT_INTENSITY = 0.9;
  const DIRECTIONAL_LIGHT_INTENSITY = 1.0; // Reduced from 1.2
  const TARGET_SCALE = 1;
  const SPHERE_TILING = 2;
  
  // Environment map for reflection
  const envMapRef = useRef(null);
  
  // Textures storage
  const texturesRef = useRef({
    diffuse: null,
    normal: null,
    roughness: null,
    displacement: null,
    ambientOcclusion: null
  });
  
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  // Available maps tracking
  const [availableMaps, setAvailableMaps] = useState({
    diffuse: false,
    normal: false,
    roughness: false,
    displacement: false,
    ambientOcclusion: false
  });
  
  // Add loading state
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingRef = useRef({
    total: 5, // Total number of possible textures
    loaded: 0,
    failed: 0
  });
  
  // Add state for complete texture view (starting as true)
  const [isCompleteView, setIsCompleteView] = useState(true);
  
  // Mouse event handlers
  const handleMouseMove = (event) => {
    if (!is3dEnabled) return;
    
    if (isDraggingRef.current && isShapeSphere) {
      handleSphereDrag(event);
      return;
    }
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const maxTilt = 10;
    
    targetTiltXRef.current = (-(y - centerY) / centerY) * maxTilt;
    targetTiltYRef.current = ((x - centerX) / centerX) * maxTilt;
  };
  
  const handleMouseLeave = () => {
    isDraggingRef.current = false;
    targetTiltXRef.current = 0;
    targetTiltYRef.current = 0;
  };
  
  const handleMouseDown = (event) => {
    if (isShapeSphere) {
      isDraggingRef.current = true;
      autoRotateRef.current = false;
      
      const rect = containerRef.current.getBoundingClientRect();
      previousMousePositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
      
      event.preventDefault();
    }
  };
  
  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };
  
  const handleSphereDrag = (event) => {
    if (!isDraggingRef.current || !sphereMeshRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const currentPosition = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    
    const deltaX = currentPosition.x - previousMousePositionRef.current.x;
    const deltaY = currentPosition.y - previousMousePositionRef.current.y;
    
    sphereRotationRef.current.y += deltaX * 0.005;
    sphereRotationRef.current.x += deltaY * 0.005;
    
    if (sphereMeshRef.current) {
      sphereMeshRef.current.rotation.y = sphereRotationRef.current.y;
      sphereMeshRef.current.rotation.x = sphereRotationRef.current.x;
    }
    
    previousMousePositionRef.current = currentPosition;
  };
  
  const toggleAutoRotate = () => {
    autoRotateRef.current = !autoRotateRef.current;
  };
  
  const toggle3dEffect = () => {
    setIs3dEnabled((prev) => !prev);
    if (!is3dEnabled) {
      targetTiltXRef.current = 0;
      targetTiltYRef.current = 0;
      if (containerRef.current) {
        containerRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
      }
    }
  };
  
  const toggleHDR = () => {
    setIsHDREnabled(prev => !prev);
    
    if (rendererRef.current) {
      if (isHDREnabled) {
        rendererRef.current.toneMapping = THREE.NoToneMapping;
        rendererRef.current.outputEncoding = THREE.LinearEncoding;
      } else {
        rendererRef.current.toneMapping = THREE.ACESFilmicToneMapping;
        rendererRef.current.outputEncoding = THREE.sRGBEncoding;
      }
    }
  };
  
  const toggleShape = () => {
    setIsShapeSphere((prev) => !prev);
    
    // Update active mesh reference
    activeMeshRef.current = isShapeSphere ? planeMeshRef.current : sphereMeshRef.current;
    
    // Hide inactive mesh and show active mesh
    if (planeMeshRef.current) planeMeshRef.current.visible = isShapeSphere;
    if (sphereMeshRef.current) sphereMeshRef.current.visible = !isShapeSphere;
    
    // Reset scale for transition animation
    if (activeMeshRef.current) {
      activeMeshRef.current.scale.set(0.5, 0.5, 0.5);
    }
    
    // Reset sphere rotation when switching to sphere
    if (!isShapeSphere) {
      sphereRotationRef.current = { x: 0, y: 0 };
      if (sphereMeshRef.current) {
        sphereMeshRef.current.rotation.x = 0;
        sphereMeshRef.current.rotation.y = 0;
      }
      autoRotateRef.current = true;
    }
  };
  
  const onDocumentMouseMove = (event) => {
    if (!rendererRef.current || !directionalLightRef.current) return;
    
    const rect = rendererRef.current.domElement.getBoundingClientRect();
    const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    directionalLightRef.current.position.x = mouseX;
    directionalLightRef.current.position.y = mouseY;
  };
  
  const animate = () => {
    // Smoothly interpolate current tilt towards the target tilt
    currentTiltXRef.current += (targetTiltXRef.current - currentTiltXRef.current) * 0.1;
    currentTiltYRef.current += (targetTiltYRef.current - currentTiltYRef.current) * 0.1;
    
    if (containerRef.current) {
      containerRef.current.style.transform = `perspective(1000px) rotateX(${currentTiltXRef.current}deg) rotateY(${currentTiltYRef.current}deg)`;
    }
    
    // Animate mesh scale for smooth transition
    if (activeMeshRef.current) {
      activeMeshRef.current.scale.x = THREE.MathUtils.lerp(activeMeshRef.current.scale.x, TARGET_SCALE, 0.05);
      activeMeshRef.current.scale.y = THREE.MathUtils.lerp(activeMeshRef.current.scale.y, TARGET_SCALE, 0.05);
      activeMeshRef.current.scale.z = THREE.MathUtils.lerp(activeMeshRef.current.scale.z, TARGET_SCALE, 0.05);
      
      // Add rotation for sphere if auto-rotate is enabled and we're not dragging
      if (isShapeSphere && sphereMeshRef.current === activeMeshRef.current && autoRotateRef.current && !isDraggingRef.current) {
        sphereMeshRef.current.rotation.y += 0.001;
      }
    }
    
    // Render the scene with post-processing
    if (composerRef.current && sceneRef.current && cameraRef.current) {
      composerRef.current.render();
    } else if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
    
    requestRef.current = requestAnimationFrame(animate);
  };
  
  // Create HDR environment
  const createEnvironmentMap = () => {
    const pmremGenerator = new THREE.PMREMGenerator(rendererRef.current);
    pmremGenerator.compileEquirectangularShader();
    
    // Create a pure white environment map
    const whiteScene = new THREE.Scene();
    whiteScene.background = new THREE.Color(0xFFFFFF);
    const cubeRenderTarget = pmremGenerator.fromScene(whiteScene);
    envMapRef.current = cubeRenderTarget.texture;
    
    return envMapRef.current;
  };
  
  // Load textures
  const loadTextures = () => {
    if (!activeMeshRef.current) return;
    
    const textureId = textureIdRef.current;
    console.log(`Texture ID: ${textureId}`);
    
    // Reset loading state
    loadingRef.current = {
      total: 5,
      loaded: 0,
      failed: 0
    };
    setLoadingProgress(0);
    
    // Forza la visualizzazione completa all'inizio
    setIsCompleteView(true);
    
    // URL builder function for ImageKit
    const getTextureUrl = (mapType, suffix) => {
      const url = `https://ik.imagekit.io/materia/${textureId}_${suffix}.jpg`;
      console.log(`Cercando mappa ${mapType}: ${url}`);
      return url;
    };
    
    // Use Three.js TextureLoader for compatibility
    const loader = new THREE.TextureLoader();
    
    // Function to update loading progress
    const updateLoadingProgress = (success) => {
      if (success) {
        loadingRef.current.loaded++;
      } else {
        loadingRef.current.failed++;
      }
      
      const progress = (loadingRef.current.loaded + loadingRef.current.failed) / loadingRef.current.total;
      setLoadingProgress(progress);
      
      // If all textures have been processed (loaded or failed), initialize the scene
      if (loadingRef.current.loaded + loadingRef.current.failed === loadingRef.current.total) {
        setIsInitialized(true);
      }
    };
    
    // Load diffuse map
    loader.load(getTextureUrl('diffuse', '0'), 
      (textureMap) => {
        textureMap.wrapS = textureMap.wrapT = THREE.RepeatWrapping;
        textureMap.encoding = THREE.sRGBEncoding;
        texturesRef.current.diffuse = textureMap;
        
        // Apply to materials
        updateMaterial('diffuse', textureMap);
        
      setAvailableMaps(prev => {
        const newState = { ...prev, diffuse: true };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(true);
    }, 
      undefined,
    (error) => {
        console.error(`Error loading diffuse map for ${textureId}:`, error);
      setAvailableMaps(prev => {
        const newState = { ...prev, diffuse: false };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(false);
      }
    );
    
    // Load normal map
    loader.load(getTextureUrl('normal', '1'),
      (textureMap) => {
        textureMap.wrapS = textureMap.wrapT = THREE.RepeatWrapping;
        texturesRef.current.normal = textureMap;
        
        updateMaterial('normal', textureMap);
        
      setAvailableMaps(prev => {
        const newState = { ...prev, normal: true };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(true);
    },
      undefined,
    (error) => {
        console.error(`Error loading normal map for ${textureId}:`, error);
      setAvailableMaps(prev => {
        const newState = { ...prev, normal: false };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(false);
      }
    );
    
    // Load roughness map
    loader.load(getTextureUrl('roughness', '2'),
      (textureMap) => {
        textureMap.wrapS = textureMap.wrapT = THREE.RepeatWrapping;
        texturesRef.current.roughness = textureMap;
        
        updateMaterial('roughness', textureMap);
        
      setAvailableMaps(prev => {
        const newState = { ...prev, roughness: true };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(true);
    },
      undefined,
    (error) => {
        console.error(`Error loading roughness map for ${textureId}:`, error);
      setAvailableMaps(prev => {
        const newState = { ...prev, roughness: false };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(false);
      }
    );
    
    // Load displacement map
    loader.load(getTextureUrl('displacement', '3'),
      (textureMap) => {
        textureMap.wrapS = textureMap.wrapT = THREE.RepeatWrapping;
        texturesRef.current.displacement = textureMap;
        
        updateMaterial('displacement', textureMap);
        
      setAvailableMaps(prev => {
        const newState = { ...prev, displacement: true };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(true);
    },
      undefined,
    (error) => {
        console.error(`Error loading displacement map for ${textureId}:`, error);
      setAvailableMaps(prev => {
        const newState = { ...prev, displacement: false };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(false);
      }
    );
    
    // Load ambient occlusion map
    loader.load(getTextureUrl('ao', '4'),
      (textureMap) => {
        textureMap.wrapS = textureMap.wrapT = THREE.RepeatWrapping;
        texturesRef.current.ambientOcclusion = textureMap;
        
        updateMaterial('ambientOcclusion', textureMap);
        
      setAvailableMaps(prev => {
        const newState = { ...prev, ambientOcclusion: true };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(true);
    },
      undefined,
    (error) => {
        console.error(`Error loading ambient occlusion map for ${textureId}:`, error);
      setAvailableMaps(prev => {
        const newState = { ...prev, ambientOcclusion: false };
        if (onMapAvailabilityChange) onMapAvailabilityChange(newState);
        return newState;
      });
        updateLoadingProgress(false);
      }
    );
  };
  
  // Update materials with advanced rendering techniques
  const updateMaterial = (mapType, textureMap) => {
    if (!planeMeshRef.current || !sphereMeshRef.current) return;
    
    console.log(`Applicando texture ${mapType} con isCompleteView=${isCompleteView}`);
    
    // Create clones for different repeat settings
    const planeTexture = textureMap.clone();
    planeTexture.repeat.set(DEFAULT_TILING, DEFAULT_TILING);
    
    const sphereTexture = textureMap.clone();
    sphereTexture.repeat.set(SPHERE_TILING, SPHERE_TILING);
    
    // Per semplicità, lasciamo questa funzione gestire solo il caso della visualizzazione completa
    if (isCompleteView) {
      // In complete view, apply all maps
      switch (mapType) {
        case 'diffuse':
          planeTexture.encoding = THREE.sRGBEncoding;
          sphereTexture.encoding = THREE.sRGBEncoding;
          planeMeshRef.current.material.map = planeTexture;
          sphereMeshRef.current.material.map = sphereTexture;
          console.log('Applied diffuse map');
          break;
        case 'normal':
          planeTexture.encoding = THREE.LinearEncoding;
          sphereTexture.encoding = THREE.LinearEncoding;
          planeMeshRef.current.material.normalMap = planeTexture;
          planeMeshRef.current.material.normalScale = new THREE.Vector2(1, 1);
          sphereMeshRef.current.material.normalMap = sphereTexture;
          sphereMeshRef.current.material.normalScale = new THREE.Vector2(1, 1);
          console.log('Applied normal map');
          break;
        case 'roughness':
          planeTexture.encoding = THREE.LinearEncoding;
          sphereTexture.encoding = THREE.LinearEncoding;
          planeMeshRef.current.material.roughnessMap = planeTexture;
          sphereMeshRef.current.material.roughnessMap = sphereTexture;
          console.log('Applied roughness map');
          break;
        case 'displacement':
          planeTexture.encoding = THREE.LinearEncoding;
          sphereTexture.encoding = THREE.LinearEncoding;
          planeMeshRef.current.material.displacementMap = planeTexture;
          planeMeshRef.current.material.displacementScale = 0.1;
          sphereMeshRef.current.material.displacementMap = sphereTexture;
          sphereMeshRef.current.material.displacementScale = 0.03;
          console.log('Applied displacement map');
          break;
        case 'ambientOcclusion':
          planeTexture.encoding = THREE.LinearEncoding;
          sphereTexture.encoding = THREE.LinearEncoding;
          planeMeshRef.current.material.aoMap = planeTexture;
          planeMeshRef.current.material.aoMapIntensity = 1.0;
          sphereMeshRef.current.material.aoMap = sphereTexture;
          sphereMeshRef.current.material.aoMapIntensity = 1.0;
          console.log('Applied ambient occlusion map');
          break;
        default:
          break;
      }
    }
    
    // Update material to apply changes
    planeMeshRef.current.material.needsUpdate = true;
    sphereMeshRef.current.material.needsUpdate = true;
  };
  
  // Funzione specifica per applicare una singola texture
  const applySingleTexture = (mapType) => {
    if (!texturesRef.current[mapType] || !planeMeshRef.current || !sphereMeshRef.current) return;
    
    // Reset all maps first
    planeMeshRef.current.material.map = null;
    planeMeshRef.current.material.normalMap = null;
    planeMeshRef.current.material.roughnessMap = null;
    planeMeshRef.current.material.displacementMap = null;
    planeMeshRef.current.material.aoMap = null;
    
    sphereMeshRef.current.material.map = null;
    sphereMeshRef.current.material.normalMap = null;
    sphereMeshRef.current.material.roughnessMap = null;
    sphereMeshRef.current.material.displacementMap = null;
    sphereMeshRef.current.material.aoMap = null;
    
    // Apply the texture as albedo (diffuse map)
    const textureMap = texturesRef.current[mapType];
    const planeTexture = textureMap.clone();
    const sphereTexture = textureMap.clone();
    
    planeTexture.repeat.set(DEFAULT_TILING, DEFAULT_TILING);
    sphereTexture.repeat.set(SPHERE_TILING, SPHERE_TILING);
    
    // Always use sRGB encoding for visualization
    planeTexture.encoding = THREE.sRGBEncoding;
    sphereTexture.encoding = THREE.sRGBEncoding;
    
    planeMeshRef.current.material.map = planeTexture;
    sphereMeshRef.current.material.map = sphereTexture;
    
    // Update material
    planeMeshRef.current.material.needsUpdate = true;
    sphereMeshRef.current.material.needsUpdate = true;
  };
  
  // Effetto per aggiornare immediatamente quando cambia activeMap
  useEffect(() => {
    if (!planeMeshRef.current || !sphereMeshRef.current) return;
    
    console.log("activeMap cambiato a:", activeMap);
    
    // Reset scales for transition effect
    planeMeshRef.current.scale.set(0.5, 0.5, 0.5);
    sphereMeshRef.current.scale.set(0.5, 0.5, 0.5);
    
    // Reset all maps first
    planeMeshRef.current.material.map = null;
    planeMeshRef.current.material.normalMap = null;
    planeMeshRef.current.material.roughnessMap = null;
    planeMeshRef.current.material.displacementMap = null;
    planeMeshRef.current.material.aoMap = null;
    
    sphereMeshRef.current.material.map = null;
    sphereMeshRef.current.material.normalMap = null;
    sphereMeshRef.current.material.roughnessMap = null;
    sphereMeshRef.current.material.displacementMap = null;
    sphereMeshRef.current.material.aoMap = null;
    
    // When a map is selected (not null), switch to single map view
    if (activeMap && texturesRef.current[activeMap]) {
      console.log("Mostrando texture singola:", activeMap);
      setIsCompleteView(false);
      applySingleTexture(activeMap);
    } else {
      // When activeMap is null, switch to complete view
      console.log("Mostrando texture completa (activeMap=null)");
      setIsCompleteView(true);
      
      // Reapply all textures
      Object.entries(texturesRef.current).forEach(([mapType, texture]) => {
        if (texture) {
          console.log(`Applicando texture ${mapType} per visualizzazione completa`);
          updateMaterial(mapType, texture);
        }
      });
    }
  }, [activeMap]); // Dipendenza solo da activeMap
  
  // Aggiungiamo un effetto per garantire che le texture vengano correttamente riapplicate
  // quando isCompleteView cambia
  useEffect(() => {
    if (!isCompleteView || !planeMeshRef.current || !sphereMeshRef.current) return;
    
    console.log("isCompleteView è true, applicando tutte le texture disponibili");
    
    // Reset all maps first
    planeMeshRef.current.material.map = null;
    planeMeshRef.current.material.normalMap = null;
    planeMeshRef.current.material.roughnessMap = null;
    planeMeshRef.current.material.displacementMap = null;
    planeMeshRef.current.material.aoMap = null;
    
    sphereMeshRef.current.material.map = null;
    sphereMeshRef.current.material.normalMap = null;
    sphereMeshRef.current.material.roughnessMap = null;
    sphereMeshRef.current.material.displacementMap = null;
    sphereMeshRef.current.material.aoMap = null;
    
    // Reapply all textures for complete view
    Object.entries(texturesRef.current).forEach(([mapType, texture]) => {
      if (texture) {
        console.log(`Applicando texture ${mapType} in seguito a cambio isCompleteView`);
        updateMaterial(mapType, texture);
      }
    });
    
  }, [isCompleteView]);
  
  // Toggle complete view - ripristina la visualizzazione completa del materiale
  const toggleCompleteView = () => {
    console.log("Ripristino texture completa tramite pulsante");
    setIsCompleteView(true);
    
    // Reset all maps in the material
    if (planeMeshRef.current && sphereMeshRef.current) {
      // Reset all maps to null first
      planeMeshRef.current.material.map = null;
      planeMeshRef.current.material.normalMap = null;
      planeMeshRef.current.material.roughnessMap = null;
      planeMeshRef.current.material.displacementMap = null;
      planeMeshRef.current.material.aoMap = null;
      
      sphereMeshRef.current.material.map = null;
      sphereMeshRef.current.material.normalMap = null;
      sphereMeshRef.current.material.roughnessMap = null;
      sphereMeshRef.current.material.displacementMap = null;
      sphereMeshRef.current.material.aoMap = null;
      
      // Reapply all textures with correct encoding
      Object.entries(texturesRef.current).forEach(([mapType, texture]) => {
        if (texture) {
          const planeTexture = texture.clone();
          const sphereTexture = texture.clone();
          
          planeTexture.repeat.set(DEFAULT_TILING, DEFAULT_TILING);
          sphereTexture.repeat.set(SPHERE_TILING, SPHERE_TILING);
          
          switch (mapType) {
            case 'diffuse':
              planeTexture.encoding = THREE.sRGBEncoding;
              sphereTexture.encoding = THREE.sRGBEncoding;
              planeMeshRef.current.material.map = planeTexture;
              sphereMeshRef.current.material.map = sphereTexture;
              break;
            case 'normal':
              planeTexture.encoding = THREE.LinearEncoding;
              sphereTexture.encoding = THREE.LinearEncoding;
              planeMeshRef.current.material.normalMap = planeTexture;
              planeMeshRef.current.material.normalScale = new THREE.Vector2(1, 1);
              sphereMeshRef.current.material.normalMap = sphereTexture;
              sphereMeshRef.current.material.normalScale = new THREE.Vector2(1, 1);
              break;
            case 'roughness':
              planeTexture.encoding = THREE.LinearEncoding;
              sphereTexture.encoding = THREE.LinearEncoding;
              planeMeshRef.current.material.roughnessMap = planeTexture;
              sphereMeshRef.current.material.roughnessMap = sphereTexture;
              break;
            case 'displacement':
              planeTexture.encoding = THREE.LinearEncoding;
              sphereTexture.encoding = THREE.LinearEncoding;
              planeMeshRef.current.material.displacementMap = planeTexture;
              planeMeshRef.current.material.displacementScale = 0.1;
              sphereMeshRef.current.material.displacementMap = sphereTexture;
              sphereMeshRef.current.material.displacementScale = 0.03;
              break;
            case 'ambientOcclusion':
              planeTexture.encoding = THREE.LinearEncoding;
              sphereTexture.encoding = THREE.LinearEncoding;
              planeMeshRef.current.material.aoMap = planeTexture;
              planeMeshRef.current.material.aoMapIntensity = 1.0;
              sphereMeshRef.current.material.aoMap = sphereTexture;
              sphereMeshRef.current.material.aoMapIntensity = 1.0;
              break;
            default:
              break;
          }
        }
      });
      
      // Update materials
      planeMeshRef.current.material.needsUpdate = true;
      sphereMeshRef.current.material.needsUpdate = true;
    }
    
    // Notify parent component to deselect all maps
    if (onMapAvailabilityChange) {
      console.log("Notificando il parent di ripristinare le texture");
      onMapAvailabilityChange({ 
        ...availableMaps, 
        active: null, // Imposta esplicitamente active a null
        diffuse: true,
        normal: true,
        roughness: true,
        displacement: true,
        ambientOcclusion: true
      });
    }
  };
  
  // Effetto per applicare tutte le texture quando il componente è inizializzato
  useEffect(() => {
    if (!isInitialized || !planeMeshRef.current || !sphereMeshRef.current) return;
    
    console.log("Componente inizializzato, applicando tutte le texture");
    
    // Reset all maps first
    planeMeshRef.current.material.map = null;
    planeMeshRef.current.material.normalMap = null;
    planeMeshRef.current.material.roughnessMap = null;
    planeMeshRef.current.material.displacementMap = null;
    planeMeshRef.current.material.aoMap = null;
    
    sphereMeshRef.current.material.map = null;
    sphereMeshRef.current.material.normalMap = null;
    sphereMeshRef.current.material.roughnessMap = null;
    sphereMeshRef.current.material.displacementMap = null;
    sphereMeshRef.current.material.aoMap = null;

    // Ensure we're in complete view mode
    setIsCompleteView(true);
    
    // Apply all loaded textures
    setTimeout(() => {
      Object.entries(texturesRef.current).forEach(([mapType, texture]) => {
        if (texture) {
          console.log(`Applicando texture ${mapType} all'inizializzazione`);
          updateMaterial(mapType, texture);
        }
      });
    }, 100); // Slight delay to ensure everything is ready
    
  }, [isInitialized]);
  
  // Initialize 3D scene with improved rendering
  useEffect(() => {
    // Create scene
    sceneRef.current = new THREE.Scene();
    // Ensure the scene background is null (transparent)
    sceneRef.current.background = null;
    
    // Create camera
    cameraRef.current = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
    cameraRef.current.position.z = 2.5;
    
    // Create renderer with improved settings
    rendererRef.current = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true, // Enable alpha for transparent background
      powerPreference: "high-performance",
      stencil: false,
      depth: true,
      premultipliedAlpha: false, // Important for correct alpha blending
      preserveDrawingBuffer: true // Help with transparency in some browsers
    });
    // Set completely transparent background
    rendererRef.current.setClearColor(0x000000, 0); // Alpha 0 makes it fully transparent
    rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio for better performance
    rendererRef.current.outputEncoding = THREE.sRGBEncoding;
    rendererRef.current.toneMapping = THREE.ACESFilmicToneMapping;
    rendererRef.current.toneMappingExposure = 1.0; // Increased from 0.8 for brighter rendering
    rendererRef.current.physicallyCorrectLights = true;
    
    // Add canvas to container
    const container = containerRef.current;
    if (container) {
      container.innerHTML = '';
      container.appendChild(rendererRef.current.domElement);
      // Remove explicit background color to ensure transparency
      container.style.backgroundColor = 'transparent';
      updateRendererSize();
    }
    
    // Create environment map for reflections (with minimal influence)
    const envMap = createEnvironmentMap();
    
    // Create materials with advanced settings
    const planeMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: DEFAULT_METALLIC,
      roughness: DEFAULT_ROUGHNESS,
      envMap: envMap,
      envMapIntensity: 0.6  // Increased from 0.3 for brighter reflections
    });
    
    const sphereMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: DEFAULT_METALLIC,
      roughness: DEFAULT_ROUGHNESS,
      envMap: envMap,
      envMapIntensity: 0.6  // Increased from 0.3 for brighter reflections
    });
    
    // Create plane geometry with higher segments
    const frustumSize = 1.5;
    const planeGeometry = new THREE.PlaneGeometry(frustumSize, frustumSize, 128, 128);
    planeGeometry.setAttribute('uv2', new THREE.BufferAttribute(planeGeometry.attributes.uv.array, 2));
    
    // Create plane mesh
    planeMeshRef.current = new THREE.Mesh(planeGeometry, planeMaterial);
    sceneRef.current.add(planeMeshRef.current);
    
    // Create sphere geometry with higher segments
    const sphereRadius = 0.8;
    const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 128, 128);
    sphereGeometry.setAttribute('uv2', new THREE.BufferAttribute(sphereGeometry.attributes.uv.array, 2));
    
    // Create sphere mesh
    sphereMeshRef.current = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sceneRef.current.add(sphereMeshRef.current);
    
    // Set default active mesh to plane
    activeMeshRef.current = planeMeshRef.current;
    sphereMeshRef.current.visible = false;
    
    // Set initial scale for smooth transition
    planeMeshRef.current.scale.set(0.5, 0.5, 0.5);
    sphereMeshRef.current.scale.set(0.5, 0.5, 0.5);
    
    // Improve lighting setup - using more ambient light and less directional for more natural texture appearance
    ambientLightRef.current = new THREE.AmbientLight(0x666666, DEFAULT_LIGHT_INTENSITY);
    sceneRef.current.add(ambientLightRef.current);
    
    directionalLightRef.current = new THREE.DirectionalLight(0xffffff, DIRECTIONAL_LIGHT_INTENSITY);
    directionalLightRef.current.position.set(0.5, 0.5, 1);
    sceneRef.current.add(directionalLightRef.current);
    
    // Add fill light for better illumination
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-0.5, -0.5, 0.5);
    sceneRef.current.add(fillLight);
    
    // Set up post-processing with transparency support
    composerRef.current = new EffectComposer(rendererRef.current);
    
    // Add Render Pass with transparent background
    const renderPass = new RenderPass(sceneRef.current, cameraRef.current);
    renderPass.clear = true; // Ensure clearing
    renderPass.clearColor = new THREE.Color(0x000000);
    renderPass.clearAlpha = 0; // Fully transparent
    composerRef.current.addPass(renderPass);
    
    // Add Output Pass with transparency 
    const outputPass = new OutputPass();
    // Ensure output pass preserves alpha
    outputPass.clear = false; // Don't clear previous passes
    outputPass.renderToScreen = true; // Ensure rendering to screen
    composerRef.current.addPass(outputPass);
    
    // Add event listeners
    rendererRef.current.domElement.addEventListener('mousemove', onDocumentMouseMove, false);
    
    // Handle window resize
    const handleResize = () => {
      updateRendererSize();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Start animation loop
    requestRef.current = requestAnimationFrame(animate);
    
    // Load textures after scene is initialized
    loadTextures();
    
    // Cleanup function
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      
      if (rendererRef.current && rendererRef.current.domElement) {
        rendererRef.current.domElement.removeEventListener('mousemove', onDocumentMouseMove);
      }
      
      window.removeEventListener('resize', handleResize);
      
      if (planeMeshRef.current) {
        sceneRef.current.remove(planeMeshRef.current);
        planeMeshRef.current.geometry.dispose();
        planeMeshRef.current.material.dispose();
      }
      
      if (sphereMeshRef.current) {
        sceneRef.current.remove(sphereMeshRef.current);
        sphereMeshRef.current.geometry.dispose();
        sphereMeshRef.current.material.dispose();
      }
      
      // Dispose of environment map
      if (envMapRef.current) {
        envMapRef.current.dispose();
      }
      
      if (composerRef.current) {
        composerRef.current.passes.forEach(pass => {
          if (pass.dispose) pass.dispose();
        });
      }
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, []); // Remove isInitialized dependency
  
  // Update renderer size
  const updateRendererSize = () => {
    if (!rendererRef.current || !containerRef.current || !cameraRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    rendererRef.current.setSize(width, height);
    
    // Explicitly set the canvas to have a transparent background
    rendererRef.current.domElement.style.backgroundColor = 'transparent';
    
    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();
    
    // Update composer size if it exists
    if (composerRef.current) {
      composerRef.current.setSize(width, height);
    }
    
    // Render the updated scene
    if (composerRef.current && sceneRef.current && cameraRef.current) {
      composerRef.current.render();
    } else if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  };
  
  // Update texture ID when texture changes
  useEffect(() => {
    if (textureData?.id) {
      textureIdRef.current = textureData.id;
      console.log(`Updated texture ID to: ${textureIdRef.current}`);
      // Reset initialization state and load new textures
      setIsInitialized(false);
      setLoadingProgress(0);
      loadTextures();
    }
  }, [textureData]);
  
  return (
    <div className="texture-viewer-3d">
      {!isInitialized && (
        <div className="texture-viewer-loading" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: isDark ? '#fff' : '#333',
          textAlign: 'center'
        }}>
          <div>Caricamento texture... {Math.round(loadingProgress * 100)}%</div>
        </div>
      )}
      <div 
        ref={containerRef} 
        className="texture-viewer-container"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      ></div>
      
      {isInitialized && (
        <div className="texture-viewer-controls">
          {!isCompleteView && (
            <Button 
              type="secondary"
              size="small"
              style={{
                borderRadius: '20px',
                backgroundColor: isDark ? '#555' : '#eaeaea',
                border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
                color: isDark ? '#fff' : '#333',
                boxShadow: isDark ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                fontWeight: 'bold'
              }}
              onClick={toggleCompleteView}
            >
              Ripristina texture
            </Button>
          )}

          <Button 
            type={is3dEnabled ? "secondary" : "default"}
            size="small"
            style={{
              borderRadius: '20px',
              backgroundColor: is3dEnabled 
                ? (isDark ? '#555' : '#eaeaea') 
                : (isDark ? '#444' : '#f5f5f5'),
              border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
              color: isDark ? '#fff' : '#333',
              boxShadow: isDark ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
            onClick={toggle3dEffect}
          >
            {is3dEnabled ? 'Disabilita 3D' : 'Abilita 3D'}
          </Button>
          
          <Button 
            type={isShapeSphere ? "secondary" : "default"}
            size="small"
            style={{
              borderRadius: '20px',
              backgroundColor: isShapeSphere 
                ? (isDark ? '#555' : '#eaeaea') 
                : (isDark ? '#444' : '#f5f5f5'),
              border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
              color: isDark ? '#fff' : '#333',
              boxShadow: isDark ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
            onClick={toggleShape}
          >
            {isShapeSphere ? 'Piano' : 'Sfera'}
          </Button>
          
          {isShapeSphere && (
            <Button 
              type={autoRotateRef.current ? "secondary" : "default"}
              size="small"
              style={{
                borderRadius: '20px',
                backgroundColor: autoRotateRef.current 
                  ? (isDark ? '#555' : '#eaeaea') 
                  : (isDark ? '#444' : '#f5f5f5'),
                border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
                color: isDark ? '#fff' : '#333',
                boxShadow: isDark ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}
              onClick={toggleAutoRotate}
            >
              {autoRotateRef.current ? 'Stop Auto' : 'Auto'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TextureViewer3D; 